import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Upload, X, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSetup = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [shortageAreas, setShortageAreas] = useState([]);
  const [formData, setFormData] = useState({
    bio: '',
    specialty: '',
    organization: '',
    shortage_area: '',
    interests: '',
    looking_for: '',
    age: '',
    gender: '',
    photos: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const userRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(userRes.data);

      const areasRes = await axios.get(`${API}/shortage-areas`);
      setShortageAreas(areasRes.data);

      const profileRes = await axios.get(`${API}/profiles/me`, { withCredentials: true });
      if (profileRes.data) {
        setFormData({
          bio: profileRes.data.bio || '',
          specialty: profileRes.data.specialty || '',
          organization: profileRes.data.organization || '',
          shortage_area: profileRes.data.shortage_area || '',
          interests: profileRes.data.interests?.join(', ') || '',
          looking_for: profileRes.data.looking_for || '',
          age: profileRes.data.age || '',
          gender: profileRes.data.gender || '',
          photos: profileRes.data.photos || []
        });
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        navigate('/');
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        setFormData(prev => ({
          ...prev,
          photos: [...prev.photos, event.target.result]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (saving) return;
    
    setSaving(true);

    try {
      const payload = {
        ...formData,
        interests: formData.interests.split(',').map(i => i.trim()).filter(Boolean),
        age: formData.age ? parseInt(formData.age) : null
      };

      await axios.post(`${API}/profiles/me`, payload, { withCredentials: true });
      toast.success('Profile saved successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Failed to save profile');
      setSaving(false); // Only reset on error, not on success (since we navigate away)
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fb]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2563eb] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            data-testid="back-button"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#2563eb] to-[#1e40af] flex items-center justify-center">
              <Heart className="w-5 h-5 text-white" fill="currentColor" />
            </div>
            <span className="font-bold text-gray-900">Healthcare Hearts</span>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Complete Your Profile</h1>
          <p className="text-gray-600 mb-8">Help others get to know you better</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Bio */}
            <div>
              <Label htmlFor="bio" className="text-gray-700 font-medium mb-2 block">Bio</Label>
              <Textarea
                id="bio"
                data-testid="bio-input"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Tell us about yourself..."
                className="min-h-[120px] resize-none"
              />
            </div>

            {/* Specialty */}
            {user?.user_type === 'healthcare_provider' && (
              <div>
                <Label htmlFor="specialty" className="text-gray-700 font-medium mb-2 block">Specialty</Label>
                <Select
                  value={formData.specialty || undefined}
                  onValueChange={(value) => setFormData({ ...formData, specialty: value })}
                >
                  <SelectTrigger data-testid="specialty-select">
                    <SelectValue placeholder="Select your specialty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Physician">Physician</SelectItem>
                    <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
                    <SelectItem value="Nurse Practitioner">Nurse Practitioner</SelectItem>
                    <SelectItem value="Physician Assistant">Physician Assistant</SelectItem>
                    <SelectItem value="Surgeon">Surgeon</SelectItem>
                    <SelectItem value="Emergency Medicine">Emergency Medicine</SelectItem>
                    <SelectItem value="Pediatrician">Pediatrician</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Organization */}
            {user?.user_type === 'healthcare_provider' && (
              <div>
                <Label htmlFor="organization" className="text-gray-700 font-medium mb-2 block">Organization</Label>
                <Input
                  id="organization"
                  data-testid="organization-input"
                  value={formData.organization}
                  onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                  placeholder="Your healthcare organization"
                />
              </div>
            )}

            {/* Shortage Area */}
            <div>
              <Label htmlFor="shortage_area" className="text-gray-700 font-medium mb-2 block">Healthcare Shortage Area</Label>
              <Select
                value={formData.shortage_area || undefined}
                onValueChange={(value) => setFormData({ ...formData, shortage_area: value })}
              >
                <SelectTrigger data-testid="shortage-area-select">
                  <SelectValue placeholder="Select your area" />
                </SelectTrigger>
                <SelectContent>
                  {shortageAreas.map((area) => (
                    <SelectItem key={area} value={area}>{area}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Age */}
              <div>
                <Label htmlFor="age" className="text-gray-700 font-medium mb-2 block">Age</Label>
                <Input
                  id="age"
                  type="number"
                  data-testid="age-input"
                  value={formData.age}
                  onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                  placeholder="Your age"
                />
              </div>

              {/* Gender */}
              <div>
                <Label htmlFor="gender" className="text-gray-700 font-medium mb-2 block">Gender</Label>
                <Select
                  value={formData.gender || undefined}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger data-testid="gender-select">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Non-binary">Non-binary</SelectItem>
                    <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Interests */}
            <div>
              <Label htmlFor="interests" className="text-gray-700 font-medium mb-2 block">Interests (comma-separated)</Label>
              <Input
                id="interests"
                data-testid="interests-input"
                value={formData.interests}
                onChange={(e) => setFormData({ ...formData, interests: e.target.value })}
                placeholder="e.g., Hiking, Reading, Travel, Cooking"
              />
            </div>

            {/* Looking For */}
            <div>
              <Label htmlFor="looking_for" className="text-gray-700 font-medium mb-2 block">Looking For</Label>
              <Textarea
                id="looking_for"
                data-testid="looking-for-input"
                value={formData.looking_for}
                onChange={(e) => setFormData({ ...formData, looking_for: e.target.value })}
                placeholder="What are you looking for in a partner?"
                className="min-h-[100px] resize-none"
              />
            </div>

            {/* Photos */}
            <div>
              <Label className="text-gray-700 font-medium mb-2 block">Photos</Label>
              <div className="grid grid-cols-3 gap-4 mb-4">
                {formData.photos.map((photo, index) => (
                  <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-gray-200">
                    <img src={photo} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      data-testid={`remove-photo-${index}`}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {formData.photos.length < 6 && (
                <label className="flex items-center justify-center gap-2 px-6 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#2563eb] hover:bg-blue-50 transition-colors">
                  <Upload className="w-5 h-5 text-gray-600" />
                  <span className="text-gray-700 font-medium">Upload Photos</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    data-testid="photo-upload"
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Submit */}
            <Button
              type="submit"
              disabled={saving}
              data-testid="save-profile-button"
              className="w-full bg-[#2563eb] hover:bg-[#1e40af] text-white py-4 rounded-lg text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Profile'}
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default ProfileSetup;
